import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FaTwitch, FaCoins, FaTrash, FaCheck, FaTimes, FaClock, FaExclamationTriangle, FaHeart } from 'react-icons/fa';
import PageHeader from '../../components/PageHeader/PageHeader';
import { Skeleton } from '../../components/Skeleton/Skeleton';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/authService';
import communityBetsService from '../../services/communityBetsService';
import './ComunidadePage.css';

const STATUS_LABELS = {
    pending: 'Pendente',
    won: 'Ganhou',
    lost: 'Perdeu',
};

const STATUS_ICONS = {
    pending: FaClock,
    won: FaCheck,
    lost: FaTimes,
};

function formatDateTime(isoString) {
    try {
        const date = new Date(isoString);
        const datePart = date.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const timePart = date.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
        return `${datePart} · ${timePart}`;
    } catch (e) {
        return '';
    }
}

function ImageLightbox({ src, onClose }) {
    return ReactDOM.createPortal(
        <div className="comunidade-lightbox-overlay" onClick={onClose}>
            <button className="comunidade-lightbox-close" onClick={onClose} aria-label="Fechar">×</button>
            <img
                className="comunidade-lightbox-image"
                src={src}
                alt="Aposta da comunidade (ampliada)"
                onClick={(e) => e.stopPropagation()}
            />
        </div>,
        document.body
    );
}

function ComunidadePage() {
    const { isAuthenticated, isLoading: authLoading, user } = useAuth();

    const [bets, setBets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [nextPage, setNextPage] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [ordering, setOrdering] = useState('likes');
    const [likingId, setLikingId] = useState(null);

    const [uploadStatus, setUploadStatus] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const fileInputRef = useRef(null);
    const [lightboxImage, setLightboxImage] = useState(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const deleteConfirmRef = useRef(null);

    const twitchError = new URLSearchParams(window.location.search).get('twitch_error');

    const loadBets = useCallback(async (currentOrdering) => {
        setLoading(true);
        setError(null);
        try {
            const data = await communityBetsService.getCommunityBets(1, currentOrdering);
            setBets(data.results || []);
            setNextPage(data.next ? 2 : null);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadBets(ordering);
    }, [loadBets, ordering]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (deleteConfirmRef.current && !deleteConfirmRef.current.contains(event.target)) {
                setDeleteConfirmId(null);
            }
        };

        if (deleteConfirmId !== null) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [deleteConfirmId]);

    useEffect(() => {
        if (isAuthenticated) {
            communityBetsService.getMyUploadStatus()
                .then(setUploadStatus)
                .catch(() => setUploadStatus(null));
        } else {
            setUploadStatus(null);
        }
    }, [isAuthenticated]);

    const handleLoadMore = async () => {
        if (!nextPage) return;
        setLoadingMore(true);
        try {
            const data = await communityBetsService.getCommunityBets(nextPage, ordering);
            setBets(prev => [...prev, ...(data.results || [])]);
            setNextPage(data.next ? nextPage + 1 : null);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleToggleLike = async (bet) => {
        if (!isAuthenticated || bet.isOwner || likingId === bet.id) return;
        setLikingId(bet.id);
        // Optimistic update
        setBets(prev => prev.map(b => (
            b.id === bet.id
                ? { ...b, likedByMe: !b.likedByMe, likesCount: b.likesCount + (b.likedByMe ? -1 : 1) }
                : b
        )));
        try {
            const updated = await communityBetsService.toggleLike(bet.id);
            setBets(prev => prev.map(b => (b.id === bet.id ? { ...b, ...updated } : b)));
        } catch (e) {
            // Revert on failure
            setBets(prev => prev.map(b => (
                b.id === bet.id
                    ? { ...b, likedByMe: bet.likedByMe, likesCount: bet.likesCount }
                    : b
            )));
            setError(e.message);
        } finally {
            setLikingId(null);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setUploadError(null);
        try {
            const created = await communityBetsService.uploadCommunityBet(file);
            setBets(prev => [created, ...prev]);
            const newStatus = await communityBetsService.getMyUploadStatus();
            setUploadStatus(newStatus);
        } catch (err) {
            setUploadError(err.message);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleStatusChange = async (id, status) => {
        try {
            const updated = await communityBetsService.updateStatus(id, status);
            setBets(prev => prev.map(b => (b.id === id ? updated : b)));
        } catch (e) {
            setError(e.message);
        }
    };

    const handleDeleteConfirm = async (id) => {
        try {
            await communityBetsService.deleteCommunityBet(id);
            setBets(prev => prev.filter(b => b.id !== id));
        } catch (e) {
            setError(e.message);
        } finally {
            setDeleteConfirmId(null);
        }
    };

    const remaining = uploadStatus?.remaining ?? null;
    const canUpload = isAuthenticated && remaining !== 0;

    return (
        <div className="page-bg-overlay">
            <main className="page-container comunidade-page-container">
                <PageHeader
                    eyebrow="Comunidade"
                    title="Apostas da Comunidade"
                    subtitle="Partilha as tuas apostas e acompanha as da comunidade."
                />

                {twitchError && (
                    <div className="comunidade-banner error">
                        Não foi possível iniciar sessão com a Twitch. Tenta novamente.
                    </div>
                )}

                <div className="comunidade-upload-box">
                    {authLoading ? null : !isAuthenticated ? (
                        <button className="btn twitch-login-btn" onClick={() => authService.loginWithTwitch()}>
                            <FaTwitch />
                            <span>Entrar com a Twitch</span>
                        </button>
                    ) : (
                        <div className="upload-controls">
                            <div className="upload-user-info">
                                {user?.twitchProfile?.avatarUrl && (
                                    <img className="upload-user-avatar" src={user.twitchProfile.avatarUrl} alt="" />
                                )}
                                <span>Olá, <strong>{user?.twitchProfile?.displayName || user?.username}</strong></span>
                            </div>
                            <label className={`btn btn-primary upload-btn ${!canUpload || uploading ? 'disabled' : ''}`}>
                                <FaCoins />
                                <span>{uploading ? 'A adicionar...' : 'Adicionar Aposta'}</span>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={handleFileChange}
                                    disabled={!canUpload || uploading}
                                    hidden
                                />
                            </label>
                            {uploadStatus && (
                                <span className="upload-remaining">
                                    {remaining > 0
                                        ? `${remaining}/2 apostas por adicionar hoje`
                                        : 'Limite diário de 2 apostas atingido'}
                                </span>
                            )}
                            {uploadError && <span className="upload-error">{uploadError}</span>}
                        </div>
                    )}
                </div>

                <div className="comunidade-sort-bar">
                    <span className="comunidade-sort-label">Ordenar por:</span>
                    <div className="comunidade-sort-options">
                        <button
                            className={`comunidade-sort-btn ${ordering === 'likes' ? 'active' : ''}`}
                            onClick={() => setOrdering('likes')}
                        >
                            Mais gostos
                        </button>
                        <button
                            className={`comunidade-sort-btn ${ordering === 'date' ? 'active' : ''}`}
                            onClick={() => setOrdering('date')}
                        >
                            Mais recentes
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="comunidade-grid" aria-label="A carregar">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div className="comunidade-card" key={i} aria-hidden="true">
                                <Skeleton height="auto" radius={0} className="comunidade-skeleton-image" />
                                <div className="comunidade-card-footer">
                                    <div className="comunidade-card-user">
                                        <Skeleton width={30} height={30} radius={999} />
                                        <div className="comunidade-card-user-text" style={{ gap: 6 }}>
                                            <Skeleton width={90} height={12} />
                                            <Skeleton width={70} height={10} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : error ? (
                    <div className="error-state">
                        <p>Erro ao carregar apostas: {error}</p>
                    </div>
                ) : bets.length === 0 ? (
                    <div className="no-bets">
                        <p>Ainda não há apostas da comunidade. Sê o primeiro a partilhar!</p>
                    </div>
                ) : (
                    <>
                        <div className="comunidade-grid">
                            {bets.map(bet => {
                                const StatusIcon = STATUS_ICONS[bet.status];
                                return (
                                    <div key={bet.id} className={`comunidade-card ${bet.status}`}>
                                        <div className="comunidade-card-image-wrapper">
                                            <img
                                                className="comunidade-card-image"
                                                src={bet.image}
                                                alt="Aposta da comunidade"
                                                loading="lazy"
                                                onClick={() => setLightboxImage(bet.image)}
                                            />
                                            <div className={`comunidade-status-badge ${bet.status}`}>
                                                {StatusIcon && <StatusIcon />}
                                                <span>{STATUS_LABELS[bet.status]}</span>
                                            </div>
                                            {user?.isStaff && (
                                                <div
                                                    className="comunidade-delete-wrapper"
                                                    ref={deleteConfirmId === bet.id ? deleteConfirmRef : null}
                                                >
                                                    <button
                                                        className="comunidade-delete-btn"
                                                        onClick={() => setDeleteConfirmId(prev => (prev === bet.id ? null : bet.id))}
                                                        aria-label="Eliminar"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                    {deleteConfirmId === bet.id && (
                                                        <div className="delete-confirm-tooltip">
                                                            <div className="delete-confirm-header">
                                                                <FaExclamationTriangle className="delete-confirm-icon" />
                                                                <span>Eliminar esta aposta?</span>
                                                            </div>
                                                            <div className="delete-confirm-actions">
                                                                <button
                                                                    className="delete-confirm-no"
                                                                    onClick={() => setDeleteConfirmId(null)}
                                                                >
                                                                    Cancelar
                                                                </button>
                                                                <button
                                                                    className="delete-confirm-yes"
                                                                    onClick={() => handleDeleteConfirm(bet.id)}
                                                                >
                                                                    Eliminar
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="comunidade-card-footer">
                                            <div className="comunidade-card-user">
                                                {bet.avatarUrl && <img className="comunidade-card-avatar" src={bet.avatarUrl} alt="" />}
                                                <div className="comunidade-card-user-text">
                                                    <span className="comunidade-card-username">{bet.displayName || bet.username}</span>
                                                    <span className="comunidade-card-date">{formatDateTime(bet.created_at)}</span>
                                                </div>
                                            </div>
                                            <button
                                                className={`comunidade-like-btn ${bet.likedByMe ? 'liked' : ''}`}
                                                onClick={() => handleToggleLike(bet)}
                                                disabled={!isAuthenticated || bet.isOwner || likingId === bet.id}
                                                title={
                                                    !isAuthenticated
                                                        ? 'Entra para dares like'
                                                        : bet.isOwner
                                                            ? 'Não podes gostar da tua própria aposta'
                                                            : bet.likedByMe ? 'Remover like' : 'Gosto'
                                                }
                                            >
                                                <FaHeart />
                                                <span>{bet.likesCount ?? 0}</span>
                                            </button>
                                        </div>
                                        {bet.isOwner && (
                                            <div className="comunidade-card-actions">
                                                {Object.keys(STATUS_LABELS).map(status => (
                                                    <button
                                                        key={status}
                                                        className={`comunidade-status-btn ${status} ${bet.status === status ? 'active' : ''}`}
                                                        onClick={() => handleStatusChange(bet.id, status)}
                                                    >
                                                        {STATUS_LABELS[status]}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {nextPage && (
                            <div className="comunidade-load-more">
                                <button className="btn load-more-btn" onClick={handleLoadMore} disabled={loadingMore}>
                                    {loadingMore ? 'A carregar...' : 'Carregar mais'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </main>
            {lightboxImage && (
                <ImageLightbox src={lightboxImage} onClose={() => setLightboxImage(null)} />
            )}
        </div>
    );
}

export default ComunidadePage;
